import { AlertTriangle, CheckCircle2, Download, FileCheck2, HeartHandshake } from 'lucide-react';
import { Button } from '../../design-system/Button';
import type { RepairResult } from '../../engine/core/types';
import { outputFilename } from '../../engine/core/filenames';
import { repairReportJson } from '../../engine/reports/serializeReport';
import { downloadBlob, downloadBuffer } from '../../utilities/download';
import { formatBytes, shortHash } from '../../utilities/format';

interface RepairResultViewProps {
  result: RepairResult;
  onInspect: () => void;
  onReset: () => void;
}

function supportUrl(): string | null {
  const value = import.meta.env.VITE_SUPPORT_URL;
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

export function RepairResultView({ result, onInspect, onReset }: RepairResultViewProps): React.JSX.Element {
  const warnings = result.validated.issues.filter((issue) => issue.severity !== 'info');
  const support = supportUrl();
  const subject = result.validated.adapter === 'word' || result.validated.adapter === 'opendocument' ? 'Document' : result.validated.adapter === 'powerpoint' ? 'Presentation' : result.validated.adapter === 'pdf' ? 'PDF' : result.validated.adapter === 'archive' ? 'Archive' : 'Workbook';
  return (
    <div className="result-view">
      <section className="result-hero">
        <div className="result-hero__icon"><CheckCircle2 /></div>
        <p className="mono label">{result.outcome.replaceAll('-', ' ').toUpperCase()}</p>
        <h1>{result.outcome === 'repaired' ? `${subject} repaired` : `${subject} repaired with warnings`}</h1>
        <p>{result.actions.length} deterministic repair action{result.actions.length === 1 ? '' : 's'} applied. The rebuilt output passed MendByte’s essential reopen validation.</p>
        <Button variant="primary" onClick={() => downloadBuffer(result.output, result.outputMime, result.outputName)}><Download />Download repaired file</Button>
        <span className="result-hero__note">New file: {result.outputName} · original unchanged</span>
      </section>
      <div className="result-grid">
        <section className="analysis-panel">
          <div className="panel-heading"><h2>What MendByte changed</h2></div>
          <ul className="action-list">
            {result.actions.map((action, index) => (
              <li key={`${action.rule.id}:${action.component}:${index}`}><FileCheck2 /><div><strong>{action.description}</strong><code>{action.component}</code><span>{action.rule.id} {action.rule.version}</span></div></li>
            ))}
          </ul>
        </section>
        <section className="analysis-panel comparison-panel">
          <div className="panel-heading"><h2>Original vs repaired</h2></div>
          <dl>
            <div><dt>Size</dt><dd>{formatBytes(result.comparison.originalSize)} → {formatBytes(result.comparison.outputSize)}</dd></div>
            <div><dt>Package parts</dt><dd>{result.comparison.originalParts} → {result.comparison.outputParts}</dd></div>
            <div><dt>Changed parts</dt><dd>{result.comparison.changedParts.length}</dd></div>
            <div><dt>Added / removed</dt><dd>{result.comparison.addedParts.length} / {result.comparison.removedParts.length}</dd></div>
          </dl>
          <code className="hash">Output SHA-256<br />{shortHash(result.comparison.outputSha256)}</code>
        </section>
      </div>
      {warnings.length > 0 ? (
        <section className="remaining-warnings"><AlertTriangle /><div><strong>{warnings.length} warning{warnings.length === 1 ? '' : 's'} remain</strong><p>MendByte preserved uncertain components rather than making destructive guesses.</p></div></section>
      ) : null}
      <div className="result-actions">
        <Button onClick={() => downloadBlob(new Blob([repairReportJson(result)], { type: 'application/json' }), outputFilename(result.outputName, 'RECOVERY_REPORT', 'json'))}>Export recovery report</Button>
        <Button variant="quiet" onClick={onInspect}>Inspect validation details</Button>
        <Button variant="quiet" onClick={onReset}>Rescue another file</Button>
      </div>
      {support ? (
        <section className="support-prompt"><HeartHandshake /><div><h2>Did MendByte save your work?</h2><p>MendByte is free and user-supported. If it helped recover something important, you can support continued development.</p></div><a className="button button--secondary" href={support} target="_blank" rel="noopener noreferrer">Support MendByte</a></section>
      ) : null}
    </div>
  );
}
